/**
 * MatchesPrintActions — Print + Download buttons for /my-matches/:sessionId.
 * Calls /api/matches/fair-ranges (GPT-5.2 powered) on click so the user gets
 * an accurate, AI-estimated fair price range — not a static formula.
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Printer, Download, FileText, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from './ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt$ = (n) => {
  if (n == null || isNaN(Number(n))) return '—';
  return `$${Number(n).toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

const buildPrintableText = (lead, ranges) => {
  const matches = lead?.matched_rvs || lead?.recommendations || [];
  const rangeMap = (ranges || []).reduce((acc, r) => { if (r.key) acc[r.key] = r; return acc; }, {});
  const dt = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  const lines = [];

  lines.push('====================================================');
  lines.push('             RV MATCH AI - DEALERSHIP CHEAT SHEET');
  lines.push('====================================================');
  lines.push(`Date generated: ${dt}`);
  lines.push(`Buyer: ${lead?.first_name || ''} ${lead?.last_name || ''}`.trim() || 'Buyer: -');
  if (lead?.email) lines.push(`Email: ${lead.email}`);
  if (lead?.phone) lines.push(`Phone: ${lead.phone}`);
  if (lead?.zipcode) lines.push(`ZIP: ${lead.zipcode}`);
  lines.push('');

  lines.push('YOUR PREFERENCES');
  lines.push('----------------------------------------------------');
  const c = lead?.matching_criteria || {};
  if (c.rv_type) lines.push(`RV type:        ${String(c.rv_type).replace(/_/g, ' ')}`);
  if (c.rv_length) lines.push(`Length range:   ${String(c.rv_length).replace(/_/g, '-')}`);
  if (c.budget_max) lines.push(`Budget cap:     ${fmt$(c.budget_max)}`);
  if (c.sleep_capacity) lines.push(`Sleeps:         ${c.sleep_capacity}`);
  if (c.camping_style) lines.push(`Camping style:  ${c.camping_style}`);
  if (Array.isArray(c.must_have_features) && c.must_have_features.length) {
    lines.push(`Must-haves:     ${c.must_have_features.join(', ')}`);
  }
  if (lead?.tow_vehicle) {
    const tv = lead.tow_vehicle;
    lines.push(`Tow vehicle:    ${[tv.year, tv.make, tv.model].filter(Boolean).join(' ')}` +
      (tv.tow_capacity ? ` (tow ~${tv.tow_capacity} lbs)` : ''));
  }
  lines.push('');

  lines.push(`TOP ${matches.length || 0} RV MATCHES`);
  lines.push('----------------------------------------------------');
  if (!matches.length) {
    lines.push('(No matches generated yet — check back in a few minutes.)');
  } else {
    matches.forEach((m, i) => {
      const rv = m.rv || m;
      const ymm =
        [rv.year, rv.make || rv.brand, rv.model].filter(Boolean).join(' ').trim()
        || rv.title
        || `Match ${i + 1}`;
      const price = rv.price || rv.listed_price || rv.asking_price;
      const key = `${rv.year || ''}|${rv.brand || rv.make || ''}|${rv.model || ''}|${rv.price || ''}`;
      const fr = rangeMap[key];
      lines.push('');
      lines.push(`${i + 1}. ${ymm}`);
      if (price) {
        lines.push(`   Listed price:   ${fmt$(price)}`);
      }
      if (fr && fr.fair) {
        lines.push(`   AI Fair value:  ${fmt$(fr.fair)}`);
        if (fr.low) lines.push(`   Strong counter: ${fmt$(fr.low)}  (open offer)`);
        if (fr.walkaway) lines.push(`   Walk-away cap:  ${fmt$(fr.walkaway)}  (max you should pay)`);
        if (fr.comment) lines.push(`   Note:           ${fr.comment}`);
      }
      const len = rv.length || rv.length_ft;
      if (len) lines.push(`   Length:         ${len} ft`);
      if (rv.sleeps) lines.push(`   Sleeps:         ${rv.sleeps}`);
      if (rv.dry_weight) lines.push(`   Dry weight:     ${rv.dry_weight} lbs`);
      const score = m.match_score ?? m.score;
      if (score != null) lines.push(`   Match score:    ${Math.round(score * (score <= 1 ? 100 : 1))}%`);
      if (rv.dealer_name || m.dealer_name) lines.push(`   Dealer:         ${rv.dealer_name || m.dealer_name}`);
      if (rv.location || m.location) lines.push(`   Location:       ${rv.location || m.location}`);
      if (rv.url || m.url) lines.push(`   Listing:        ${rv.url || m.url}`);
    });
  }

  lines.push('');
  lines.push('NEGOTIATION NOTES');
  lines.push('----------------------------------------------------');
  lines.push('* Open with the "Strong counter" price above. Most dealers');
  lines.push('  will meet you between counter and fair value.');
  lines.push('* Ask for the OUT-THE-DOOR price in writing before talking trade.');
  lines.push('* Skip dealer add-ons (etching, fabric protection, vehicle');
  lines.push('  insurance) unless you priced them elsewhere first.');
  lines.push('* If they will not move below "Walk-away cap", walk and');
  lines.push('  follow up in 48 hours - the offer will improve.');
  lines.push('');
  lines.push('Generated by RV Match AI');
  return lines.join('\n');
};

const MatchesPrintActions = ({ lead, sessionId, isDark }) => {
  const [busy, setBusy] = useState(null); // 'print' | 'download' | null

  const fetchRanges = async () => {
    try {
      const res = await axios.post(`${API}/matches/fair-ranges`, { session_id: sessionId });
      return res.data?.ranges || [];
    } catch (e) {
      console.warn('Fair-range fetch failed; falling back to listed price only', e);
      return [];
    }
  };

  const handleDownload = async () => {
    setBusy('download');
    const ranges = await fetchRanges();
    const text = buildPrintableText(lead, ranges);
    const blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `rv-matches-${(lead?.first_name || 'list').toLowerCase()}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    setBusy(null);
    toast.success('Cheat sheet downloaded with AI-estimated fair prices.');
  };

  const handlePrint = async () => {
    setBusy('print');
    const ranges = await fetchRanges();
    const text = buildPrintableText(lead, ranges);
    const w = window.open('', '_blank', 'width=820,height=900');
    if (!w) {
      setBusy(null);
      toast.error('Please allow pop-ups to print.');
      return;
    }
    w.document.write(`<!doctype html><html><head><title>RV Matches - Dealership Cheat Sheet</title>
      <meta charset="utf-8"/>
      <style>
        body { font-family: 'SF Mono', Menlo, Consolas, monospace; padding: 24px; color:#111; line-height:1.55; }
        pre { white-space: pre-wrap; word-break: break-word; font-size: 13px; }
        @media print { body { padding: 12px; } }
      </style>
    </head><body><pre>${text.replace(/[<>&]/g, ch => ({ '<': '&lt;', '>': '&gt;', '&': '&amp;' })[ch])}</pre>
    <script>window.onload = () => { setTimeout(() => window.print(), 250); };<\/script>
    </body></html>`);
    w.document.close();
    setBusy(null);
  };

  const matchCount = (lead?.matched_rvs || lead?.recommendations || []).length;
  const disabled = matchCount === 0;

  return (
    <div
      className={`rounded-2xl ${isDark ? 'bg-gray-900/60 border border-gray-800' : 'bg-white border border-gray-200'} p-4 sm:p-5 shadow-sm`}
      data-testid="matches-print-actions"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div className="flex items-center gap-2">
          <FileText className="w-4 h-4 text-[#F2A900]" />
          <p className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
            Take it to the dealership
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            type="button"
            onClick={handlePrint}
            disabled={disabled || !!busy}
            variant="outline"
            className="font-semibold"
            data-testid="matches-print-btn"
          >
            {busy === 'print'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Printer className="w-4 h-4 mr-2" />}
            Print
          </Button>
          <Button
            type="button"
            onClick={handleDownload}
            disabled={disabled || !!busy}
            className="bg-[#F2A900] hover:bg-[#E89400] text-black font-semibold"
            data-testid="matches-download-btn"
          >
            {busy === 'download'
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Download className="w-4 h-4 mr-2" />}
            Download .txt
          </Button>
        </div>
      </div>
      {!disabled && (
        <p className={`text-xs mt-2 ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
          We use GPT-5.2 to estimate a fair counter-offer, fair value, and walk-away cap for each match — accurate, real-market numbers ready for the dealership.
        </p>
      )}
    </div>
  );
};

export default MatchesPrintActions;
